import React from 'react';
import styled from 'styled-components';

const colors = require('common/lib/colors/colors.less');
import { useLightText } from 'common/lib/colors';
import fontFamily from '../data/font-family';
import TrelloIcon from '../icons/TrelloIcon';

import { Board as BaseBoard, BoardProps } from '../shared/Board';
import { ActionPanel } from './ActionPanel';
import { Card, CardLink } from './Card';

const BoardName = styled.div`
  font-weight: bold;
`;

const TeamName = styled.div`
  font-weight: bold;
`;

const ListName = styled.div`
  font-weight: normal;
`;

const NameUrl = styled.a`
  text-decoration: none;
  color: inherit;

  :hover {
    color: inherit;
    text-decoration: underline;
  }
`;

interface ListNameSectionProps {
  displayLogo?: boolean;
}

const ListNameSection = styled<ListNameSectionProps, 'div'>('div')`
  display: flex;
  font-family: ${fontFamily};
  font-size: 12px;
  height: 16px;
  line-height: 16px;
  letter-spacing: -0.2px;
  padding: ${({ displayLogo }) => (displayLogo ? '' : '4px')} 8px;

  ${ListName} {
    flex: 1;
  }

  ${TeamName}, ${BoardName}, ${ListName} {
    display: inline-block;
    font-size: inherit;
    height: 100%;
    margin: 0;
    overflow: hidden;
    position: relative;
    text-overflow: ellipsis;
    top: 1px;
    white-space: nowrap;
  }
`;

const TeamBoardAndListNameSection = ListNameSection.extend``;

const BoardAndListNameSection = ListNameSection.extend``;

const TrelloLogoSpan = styled.span`
  display: inline-block;
  height: 0;
  height: 16px;
  margin-right: 8px;
  width: 16px;
`;

const TrelloLogo = (props: React.BaseHTMLAttributes<HTMLSpanElement>) => (
  <TrelloLogoSpan {...props}>
    <TrelloIcon height={16} width={16} />
  </TrelloLogoSpan>
);

export const Board = styled<BoardProps>(BaseBoard)`
  background-position: bottom;
  background-size: ${({ headerBgColor }) =>
    headerBgColor ? '100% 40px, cover' : 'cover'};

  ${Card}, ${CardLink} {
    margin: 8px 8px 0 8px;
    vertical-align: top;
    width: calc(100% - 16px);
  }

  ${BoardAndListNameSection}, ${TeamBoardAndListNameSection}, ${ListNameSection} {
    color: ${({ bgColor, headerBgColor }) => {
      const color = headerBgColor || bgColor || colors['trello-blue-500'];

      return useLightText(color) ? colors.white : colors['near-black'];
    }}
  }

  ${ActionPanel} {
    border-radius: 0;
    border-top: 0;
  }
`;

interface ListNameOnlyProps extends ListNameSectionProps {
  listName: string;
}

export const ListNameOnly = ({ listName, ...props }: ListNameOnlyProps) => (
  <ListNameSection {...props}>
    <ListName>{listName}</ListName>
  </ListNameSection>
);

interface BoardAndListNameProps {
  boardName?: string;
  boardUrl?: string;
  displayLogo?: boolean;
  listName?: string;
}

export const BoardAndListName = ({
  boardName,
  boardUrl,
  displayLogo = false,
  listName,
  ...props
}: BoardAndListNameProps) => (
  <BoardAndListNameSection displayLogo={displayLogo} {...props}>
    {displayLogo && <TrelloLogo />}

    <BoardName>
      {boardUrl ? <NameUrl href={boardUrl}>{boardName}</NameUrl> : boardName}
      {listName ? ':' : null}
      &nbsp;
    </BoardName>
    <ListName>{listName}</ListName>
  </BoardAndListNameSection>
);

interface TeamBoardAndListNameProps extends ListNameSectionProps {
  boardName?: string;
  boardUrl?: string;
  teamName?: string;
  teamUrl?: string;
  listName?: string | null;
  title?: string; // TODO: Doesn't appear to be used, but is being passed in
}

export const TeamBoardAndListName = ({
  boardName,
  boardUrl,
  teamName,
  teamUrl,
  displayLogo = false,
  listName,
  ...props
}: TeamBoardAndListNameProps) => (
  <TeamBoardAndListNameSection displayLogo={displayLogo} {...props}>
    {displayLogo && <TrelloLogo />}

    <TeamName>
      {teamUrl ? <NameUrl href={teamUrl}>{teamName}</NameUrl> : teamName}
      {teamName ? ' |' : null}
      &nbsp;
    </TeamName>
    <BoardName>
      {boardUrl ? <NameUrl href={boardUrl}>{boardName}</NameUrl> : boardName}
      {listName ? ':' : null}
      &nbsp;
    </BoardName>
    <ListName>{listName}</ListName>
  </TeamBoardAndListNameSection>
);



// WEBPACK FOOTER //
// ./app/common/components/canonical-components/canonical-card/Board.tsx